import { memberInviteSchema } from "@mailhand/core/schemas";
import { type SubmitEvent, useState } from "react";
import { moveOffTenant } from "../../app/leaveTenant.js";
import { useApi, useSession, useSessions } from "../../app/services.js";
import { sessionEmail } from "../../app/sessionEmail.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { shortTime } from "../../lib/format.js";
import { Badge, Button, Card, CardHeader, Field, Input } from "../../ui/index.js";

export function MembersSection() {
  const api = useApi();
  const { view, reloadView } = useTenant();
  const session = useSession();
  const sessions = useSessions();
  const task = useTask();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [leaving, setLeaving] = useState(false);
  const me = session === null ? null : sessionEmail(session.session);
  const now = new Date();

  async function invite(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const parsed = memberInviteSchema.safeParse({ email: email.trim() });
    if (!parsed.success) {
      setError("Type an email address.");
      return;
    }
    setError(undefined);
    const ok = await task.run(async () => {
      await api.call("memberInvite", { body: parsed.data });
      await reloadView();
    }, `Invite sent to ${parsed.data.email}.`);
    if (ok) setEmail("");
  }

  async function revoke(id: string): Promise<void> {
    await task.run(async () => {
      await api.call("inviteRevoke", { params: { id } });
      await reloadView();
    }, "Invite revoked.");
  }

  async function leave(address: string): Promise<void> {
    await task.run(async () => {
      await api.call("memberRemove", { params: { email: address } });
      await moveOffTenant(api, sessions, view.tenant.id);
    }, `You left ${view.tenant.name}.`);
  }

  return (
    <Card id="members">
      <CardHeader eyebrow="Members" />
      <ul className="rows">
        {view.members.map((member) => (
          <li key={member.email} className="row">
            <span className="row-main">
              <span>{member.email}</span>
              <span className="mono muted small">joined {shortTime(member.joinedAt, now)}</span>
            </span>
            {member.email === me ? (
              leaving ? (
                <span className="button-row">
                  <Button size="sm" variant="danger" busy={task.busy} onClick={() => void leave(member.email)}>
                    Yes, leave
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setLeaving(false); }}>
                    Stay
                  </Button>
                </span>
              ) : (
                <span className="button-row">
                  <Badge tone="outline">You</Badge>
                  <Button size="sm" variant="ghost" onClick={() => { setLeaving(true); }}>
                    Leave
                  </Button>
                </span>
              )
            ) : null}
          </li>
        ))}
        {view.invites.map((pending) => (
          <li key={pending.id} className="row">
            <span className="row-main">
              <span>{pending.email}</span>
              <span className="mono muted small">invited by {pending.invitedBy}</span>
            </span>
            <span className="button-row">
              <Badge tone="amber">Pending</Badge>
              <Button size="sm" variant="ghost" disabled={task.busy} onClick={() => void revoke(pending.id)}>
                Revoke
              </Button>
            </span>
          </li>
        ))}
      </ul>
      <form className="inline-form" onSubmit={(event) => void invite(event)} noValidate>
        <Field label="Invite by email" error={error}>
          {({ id, describedBy, invalid }) => (
            <Input id={id} type="email" placeholder="jo@yourbusiness.com" value={email} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setEmail(event.target.value); }} />
          )}
        </Field>
        <Button type="submit" busy={task.busy}>
          Send invite
        </Button>
      </form>
    </Card>
  );
}
